import React, { useState } from 'react';
import { Edit2, Key, Save, X, Eye, EyeOff, Building2, Phone, Mail, MapPin, AlertCircle, Check } from 'lucide-react';
import { Site } from '../../types';
import { Button, Modal } from '../shared';

interface SiteManagementProps {
    sites: Site[];
    onUpdateSite: (site: Site) => void;
    readOnly?: boolean;
}

export const SiteManagement: React.FC<SiteManagementProps> = ({ sites, onUpdateSite, readOnly = false }) => {
    const [selectedSite, setSelectedSite] = useState<Site | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [editedSite, setEditedSite] = useState<Site | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterRegion, setFilterRegion] = useState('ALL');

    const regions = ['ALL', ...Array.from(new Set(sites.map(s => s.region)))];

    const filteredSites = sites.filter(site => {
        const matchesSearch = site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            site.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
            site.address.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesRegion = filterRegion === 'ALL' || site.region === filterRegion;
        return matchesSearch && matchesRegion;
    });

    const handleEdit = (site: Site) => {
        setSelectedSite(site);
        setEditedSite({ ...site });
        setShowModal(true);
    };

    const handleSave = () => {
        if (!editedSite) return;

        // Validate
        if (!editedSite.password || editedSite.password.length < 3) {
            alert('La contraseña debe tener al menos 3 caracteres');
            return;
        }

        onUpdateSite(editedSite);
        setShowModal(false);
        setSelectedSite(null);
        setEditedSite(null);
        setShowPassword(false);
    };

    const handleCancel = () => {
        setShowModal(false);
        setSelectedSite(null);
        setEditedSite(null);
        setShowPassword(false);
    };

    const generateRandomPassword = () => {
        if (!editedSite) return;
        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        const password = Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
        setEditedSite({ ...editedSite, password });
        setShowPassword(true);
    };

    return (
        <div className="bg-white p-6 rounded-xl border shadow-sm">
            <div className="mb-6">
                <h2 className="text-xl font-bold mb-1">Gestión de Tiendas y Sitios</h2>
                <p className="text-gray-500 text-sm">Administra contraseñas, contactos y datos de cada tienda</p>
            </div>

            {/* Filters */}
            <div className="mb-6 flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                    <input
                        type="text"
                        placeholder="Buscar por nombre, código o dirección..."
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <div>
                    <select
                        className="px-4 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-black"
                        value={filterRegion}
                        onChange={(e) => setFilterRegion(e.target.value)}
                    >
                        {regions.map(region => (
                            <option key={region} value={region}>
                                {region === 'ALL' ? 'Todas las Regiones' : `Región ${region}`}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-xs font-bold text-blue-700 uppercase mb-1">Total Sitios</p>
                    <p className="text-2xl font-bold text-blue-900">{filteredSites.length}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <p className="text-xs font-bold text-purple-700 uppercase mb-1">Región RM</p>
                    <p className="text-2xl font-bold text-purple-900">{sites.filter(s => s.region === 'RM').length}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <p className="text-xs font-bold text-green-700 uppercase mb-1">Regiones</p>
                    <p className="text-2xl font-bold text-green-900">{sites.filter(s => s.region !== 'RM').length}</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <p className="text-xs font-bold text-orange-700 uppercase mb-1">Corporativo</p>
                    <p className="text-2xl font-bold text-orange-900">{sites.filter(s => s.code.includes('098') || s.code.includes('099')).length}</p>
                </div>
            </div>

            {/* Sites Table */}
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 text-gray-500 uppercase font-bold text-xs">
                        <tr>
                            <th className="px-4 py-3">Código</th>
                            <th className="px-4 py-3">Nombre</th>
                            <th className="px-4 py-3">Región</th>
                            <th className="px-4 py-3">Contacto</th>
                            <th className="px-4 py-3">Email</th>
                            <th className="px-4 py-3">Teléfono</th>
                            {!readOnly && <th className="px-4 py-3">Acciones</th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredSites.map(site => (
                            <tr key={site.id} className="hover:bg-gray-50 transition">
                                <td className="px-4 py-3 font-mono font-bold text-blue-700">{site.code}</td>
                                <td className="px-4 py-3">
                                    <div className="font-medium text-gray-900">{site.name}</div>
                                    <div className="text-xs text-gray-500">{site.address}</div>
                                </td>
                                <td className="px-4 py-3">
                                    <span className="bg-gray-100 px-2 py-1 rounded text-xs font-bold">{site.region}</span>
                                </td>
                                <td className="px-4 py-3 text-gray-700">{site.contact}</td>
                                <td className="px-4 py-3 text-gray-600 text-xs">{site.email}</td>
                                <td className="px-4 py-3 text-gray-600 text-xs font-mono">{site.phone}</td>
                                {!readOnly && (
                                    <td className="px-4 py-3">
                                        <Button onClick={() => handleEdit(site)} size="sm" variant="secondary">
                                            <Edit2 size={14} className="mr-1" /> Editar
                                        </Button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Edit Modal */}
            {showModal && editedSite && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in">
                    <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4">
                        <div className="bg-gray-100 px-6 py-4 border-b flex justify-between items-center">
                            <div>
                                <h3 className="font-bold text-gray-900">Editar Tienda</h3>
                                <p className="text-xs text-gray-500">{editedSite.code} - {editedSite.name}</p>
                            </div>
                            <button onClick={handleCancel} className="text-gray-500 hover:text-black transition">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                            {/* Password Section */}
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex items-start mb-3">
                                    <Key className="text-yellow-700 mr-2 mt-0.5" size={20} />
                                    <div className="flex-1">
                                        <h4 className="font-bold text-yellow-900 mb-1">Contraseña de Acceso</h4>
                                        <p className="text-xs text-yellow-700">Esta contraseña es usada por el personal de tienda para ingresar al sistema</p>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            className="w-full px-4 py-2 border rounded-lg pr-20 focus:ring-2 focus:ring-yellow-500 font-mono"
                                            placeholder="Contraseña..."
                                            value={editedSite.password || ''}
                                            onChange={(e) => setEditedSite({ ...editedSite, password: e.target.value })}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>

                                    <button
                                        onClick={generateRandomPassword}
                                        className="text-sm text-yellow-700 hover:text-yellow-900 font-medium flex items-center"
                                    >
                                        <Key size={14} className="mr-1" /> Generar Contraseña Aleatoria
                                    </button>

                                    {editedSite.password && editedSite.password.length < 3 && (
                                        <div className="flex items-center text-red-600 text-xs">
                                            <AlertCircle size={12} className="mr-1" />
                                            Mínimo 3 caracteres
                                        </div>
                                    )}

                                    {editedSite.password && editedSite.password.length >= 3 && (
                                        <div className="flex items-center text-green-600 text-xs">
                                            <Check size={12} className="mr-1" />
                                            Contraseña válida
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div>
                                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                                    <Building2 size={18} className="mr-2" />
                                    Información de Contacto
                                </h4>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Contacto Principal</label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black"
                                            value={editedSite.contact}
                                            onChange={(e) => setEditedSite({ ...editedSite, contact: e.target.value })}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                            <Phone size={14} className="mr-1" /> Teléfono
                                        </label>
                                        <input
                                            type="tel"
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black font-mono"
                                            value={editedSite.phone}
                                            onChange={(e) => setEditedSite({ ...editedSite, phone: e.target.value })}
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                            <Mail size={14} className="mr-1" /> Email
                                        </label>
                                        <input
                                            type="email"
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black"
                                            value={editedSite.email}
                                            onChange={(e) => setEditedSite({ ...editedSite, email: e.target.value })}
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                            <MapPin size={14} className="mr-1" /> Dirección
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black"
                                            value={editedSite.address}
                                            onChange={(e) => setEditedSite({ ...editedSite, address: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Site Info (Read-only) */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="font-bold text-gray-700 mb-2 text-sm">Información del Sitio (Solo lectura)</h4>
                                <div className="grid md:grid-cols-3 gap-3 text-sm">
                                    <div>
                                        <span className="text-gray-500 text-xs">Código:</span>
                                        <p className="font-mono font-bold">{editedSite.code}</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-500 text-xs">ID Sistema:</span>
                                        <p className="font-mono">{editedSite.id}</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-500 text-xs">Región:</span>
                                        <p className="font-bold">{editedSite.region}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 px-6 py-4 border-t flex justify-between">
                            <Button onClick={handleCancel} variant="secondary">
                                Cancelar
                            </Button>
                            <Button onClick={handleSave} variant="primary">
                                <Save size={16} className="mr-2" /> Guardar Cambios
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
